/**
 * Compatibility wrapper — makes NativeImapClient look like the old ImapClient API.
 * This allows mailx-imap to switch to the native client without rewriting all call sites.
 * Each method opens the mailbox, does the operation, and returns. No persistent state.
 */

import { NativeImapClient, type NativeFolder } from "./imap-native.js";
import { FetchedMessage } from "./fetched-message.js";
import type { ImapClientConfig } from "./types.js";
import type { TransportFactory } from "./transport.js";
import type * as proto from "./imap-protocol.js";

/** Special folder detection result */
// Later we may need special casing
export interface SpecialFolders {
    inbox?: string;
    sent?: string;
    trash?: string;
    drafts?: string;
    spam?: string;
    junk?: string;
    archive?: string;
}

// We may need some special cases in the future
const defaultFolders: SpecialFolders = {
    inbox: "INBOX",            // Uppercase -- we'll need a reality check
    sent: "Sent",
    trash: "Trash",
    drafts: "Drafts",
    spam: "_Spam",
    junk: "_Spam",
    archive: "Archive"
}

/**
 * Compatibility IMAP client — wraps NativeImapClient with the old ImapClient API.
 * Each method creates its own select/operation/close cycle.
 */
export class CompatImapClient {
    private native: NativeImapClient;

    constructor(config: ImapClientConfig, transportFactory: TransportFactory) {
        this.native = new NativeImapClient(config, transportFactory);
    }

    /** Connect and authenticate */
    async connect(): Promise<void> {
        await this.native.connect();
    }

    /**
     * Ensure connected (lazy connect). Checks both the native client's connected
     * flag AND the transport's connected state. The transport flag catches cases
     * where the socket died without a close/error event reaching NativeImapClient
     * (common on Android where the bridge may not relay all TCP events).
     */
    private async ensureConnected(): Promise<void> {
        if (!this.native.connected || !this.native.transportConnected) {
            // Force a fresh transport — the old one may be in a bad state.
            this.native.resetTransport();
            await this.connect();
        }
    }

    async logout(): Promise<void> {
        await this.native.logout();
    }

    /** Get folder list */
    async getFolderList(): Promise<NativeFolder[]> {
        await this.ensureConnected();
        return this.native.listFolders();
    }

    /** Extract special folders from folder list */
    getSpecialFolders(folders: NativeFolder[]): SpecialFolders {
        // Two-pass detection so RFC 6154 \Drafts / \Sent / \Trash / \Junk /
        // \Archive flags always win over name-based guesses. Previous single-pass
        // `else if (flag || name)` could tag a stray folder named "DRAFT" as the
        // drafts folder on Gmail, beating out [Imap]/Drafts which has the real
        // \Drafts flag — Gmail then rejects APPEND to "DRAFT" as NONEXISTENT.
        let result: SpecialFolders = {};
        const leafName = (f: NativeFolder): string => {
            const delim = (f as any).delimiter || ".";
            return (f.path.split(delim).at(-1) ?? f.path).toLowerCase();
        };
        const flagSet = (f: NativeFolder): Set<string> => new Set(f.flags.map(fl => fl.toLowerCase()));
        // const df = 'y:\\temp\\folders.csv';
        // fs.writeFileSync(df, "path,flags\n" + folders.map(f => `${f.path},"${f.flags.join(";")}"`).join("\n"));
        // console.log(`Wrote folder list to ${df}`);

        // RFC 6154 flags only — authoritative if present
        // Note that Dovecot does not have them but we check INBOX for the case
        for (const f of folders) {
            const flags = flagSet(f);

            // Debubing 
            const dbgignore = ['\\haschildren', '\\hasnochildren', '\\noselect', '\\unmarked'];
            const dbgfgall = Array.from(flags);
            const dbgfg = dbgfgall.filter(fl => !dbgignore.includes(fl));

            if (flags.has("\\inbox") || leafName(f) === "inbox")
                result.inbox ??= f.path;
            if (flags.has("\\sent"))
                result.sent ??= f.path;
            if (flags.has("\\trash"))
                result.trash ??= f.path;
            if (flags.has("\\drafts"))
                result.drafts ??= f.path;
            if (flags.has("\\archive"))
                result.archive ??= f.path;
            if (flags.has("\\junk"))
                result.junk = f.path;
            if (flags.has("\\spam"))
                result.spam ??= f.path;
        }

        result = { ...defaultFolders, ...result };   // Fill in any missing defaults

        // // Pass 2: exact leaf-name fallback for servers that don't advertise flags
        // const sentNames = new Set(["sent", "sent items", "sent mail"]);
        // const trashNames = new Set(["trash", "deleted items", "deleted"]);
        // const draftsNames = new Set(["drafts", "draft"]);
        // const archiveNames = new Set(["archive", "archives"]);
        // const junkNames = new Set(["spam", "junk", "junk email", "junk e-mail"]);
        // for (const f of folders) {
        //     const name = leafName(f);
        //     if (sentNames.has(name)) result.sent ??= f.path;
        //     if (trashNames.has(name)) result.trash ??= f.path;
        //     if (draftsNames.has(name)) result.drafts ??= f.path;
        //     if (archiveNames.has(name)) result.archive ??= f.path;
        //     if (junkNames.has(name)) {
        //         result.junk ??= f.path;
        //         result.spam ??= f.path;
        //     }
        // }
        return result;
    }

    /** Fetch messages since a UID in a mailbox */
    async fetchMessagesSinceUid(mailbox: string, sinceUid: number, options?: { source?: boolean }): Promise<FetchedMessage[]> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        const msgs = await this.native.fetchSinceUid(sinceUid, options);
        await this.native.closeMailbox();
        return msgs.map(m => new FetchedMessage(m));
    }

    /** Batch-fetch bodies for many UIDs in one folder on one connection. Streams
     *  each body through `onBody` as it arrives. No per-message round trips —
     *  one SELECT, one UID FETCH, streaming response. Required by mailx-imap's
     *  batch prefetch path. */
    async fetchBodiesBatch(mailbox: string, uids: number[], onBody: (uid: number, source: string) => void): Promise<void> {
        if (uids.length === 0) return;
        await this.ensureConnected();
        await this.native.fetchBodiesBatch(mailbox, uids, onBody);
    }

    /** Fetch messages by date range. Optional onChunk callback for incremental processing. */
    async fetchMessageByDate(mailbox: string, start: Date, end?: Date, options?: { source?: boolean }, onChunk?: (msgs: FetchedMessage[]) => void): Promise<FetchedMessage[]> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        const chunkCb = onChunk ? (raw: import("./imap-native.js").NativeFetchedMessage[]) => onChunk(raw.map(m => new FetchedMessage(m))) : undefined;
        const msgs = await this.native.fetchByDate(start, end, options, chunkCb);
        await this.native.closeMailbox();
        return msgs.map(m => new FetchedMessage(m));
    }

    /** Fetch the most recent N messages by sequence number — sidesteps
     *  SEARCH SINCE which can take minutes on cold Dovecot mailboxes. Used
     *  by mailx-imap's first-sync path so the user sees something fast even
     *  on a wide folder tree. The 30-day backfill is a follow-up step. */
    async fetchLatestN(mailbox: string, n: number, options?: { source?: boolean }, onChunk?: (msgs: FetchedMessage[]) => void): Promise<FetchedMessage[]> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        const chunkCb = onChunk ? (raw: import("./imap-native.js").NativeFetchedMessage[]) => onChunk(raw.map(m => new FetchedMessage(m))) : undefined;
        const msgs = await this.native.fetchLatestN(n, options, chunkCb);
        await this.native.closeMailbox();
        return msgs.map(m => new FetchedMessage(m));
    }

    /** Fetch a single message by UID */
    async fetchMessageByUid(mailbox: string, uid: number, options?: { source?: boolean }): Promise<FetchedMessage | null> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        const msg = await this.native.fetchMessage(uid, options);
        await this.native.closeMailbox();
        return msg ? new FetchedMessage(msg) : null;
    }

    /** Get message count via STATUS (does not require SELECT) */
    async getMessagesCount(mailbox: string): Promise<number> {
        await this.ensureConnected();
        return this.native.getMessageCount(mailbox);
    }

    /** Get all UIDs in a mailbox */
    async getUids(mailbox: string): Promise<number[]> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        const uids = await this.native.getUids();
        await this.native.closeMailbox();
        return uids;
    }

    /** Get UIDs whose INTERNALDATE is on/after `since`. Bounded version of
     *  getUids — returns only the date window the caller cares about
     *  instead of the entire folder. Lets set-diff reconciliation scope
     *  itself to "messages from the last N days" rather than enumerating
     *  every UID in a 134k-message folder. */
    async getUidsSince(mailbox: string, since: Date): Promise<number[]> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        const d = `${since.getUTCDate()}-${months[since.getUTCMonth()]}-${since.getUTCFullYear()}`;
        const uids = await this.native.search(`SINCE ${d}`);
        await this.native.closeMailbox();
        return uids;
    }

    /** Fetch messages — supports two calling conventions for compatibility:
     *
     *    New:  fetchMessages(mailbox, "100:200")           — UID range string
     *    Old:  fetchMessages(mailbox, endSeq, count)       — sequence-number range (iflow compat)
     *
     *  The old form computes UID range "start:end" from (end - count + 1) : end,
     *  matching the legacy iflow/imapflow fetchMessages(mailbox, end, count) API
     *  used by the puller. */
    async fetchMessages(mailbox: string, uidRange: string, options?: { source?: boolean }): Promise<FetchedMessage[]>;
    async fetchMessages(mailbox: string, end: number, count: number, options?: { source?: boolean }): Promise<FetchedMessage[]>;
    async fetchMessages(
        mailbox: string,
        rangeOrEnd: string | number,
        countOrOptions?: number | { source?: boolean },
        maybeOptions?: { source?: boolean },
    ): Promise<FetchedMessage[]> {
        let range: string;
        let options: { source?: boolean } | undefined;
        if (typeof rangeOrEnd === "number") {
            // Legacy (iflow) calling convention: (mailbox, endSeq, count, options?)
            const end = rangeOrEnd;
            const count = typeof countOrOptions === "number" ? countOrOptions : 1;
            const start = Math.max(1, end - count + 1);
            range = `${start}:${end}`;
            options = maybeOptions;
        } else {
            // New calling convention: (mailbox, uidRange, options?)
            range = rangeOrEnd;
            options = typeof countOrOptions === "object" ? countOrOptions : undefined;
        }
        await this.ensureConnected();
        await this.native.select(mailbox);
        const msgs = await this.native.fetchMessages(range, options);
        await this.native.closeMailbox();
        return msgs.map(m => new FetchedMessage(m));
    }

    /** Search messages in a mailbox.
     *  Criteria: from/to/cc/subject/body/text (string or string[], each key
     *  repeats and ANDs), since/before (Date), seen/unseen/flagged/answered/
     *  draft (boolean flags), `not` (criteria object or array — each becomes
     *  a NOT'd key group), `or` (array of ≥2 criteria objects — compiled to
     *  IMAP's binary prefix OR chain). */
    async searchMessages(mailbox: string, criteria: any): Promise<number[]> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        const searchStr = buildSearchString(criteria || {});
        const uids = await this.native.search(searchStr);
        await this.native.closeMailbox();
        return uids;
    }

    /** Search by header value — returns matching UIDs */
    async searchByHeader(mailbox: string, headerName: string, headerValue: string): Promise<number[]> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        const uids = await this.native.search(`HEADER ${headerName} "${headerValue}"`);
        await this.native.closeMailbox();
        return uids;
    }

    /** Delete a message by UID */
    async deleteMessageByUid(mailbox: string, uid: number): Promise<void> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        await this.native.deleteMessage(uid);
        await this.native.closeMailbox();
    }

    /** Move a message between mailboxes (same server) */
    async moveMessage(msg: any, fromMailbox: string, toMailbox: string): Promise<void> {
        await this.ensureConnected();
        await this.native.select(fromMailbox);
        await this.native.moveMessage(msg.uid, toMailbox);
        await this.native.closeMailbox();
    }

    /** Add flags to a message */
    async addFlags(mailbox: string, uid: number, flags: string[]): Promise<void> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        await this.native.addFlags(uid, flags);
        await this.native.closeMailbox();
    }

    /** Remove flags from a message */
    async removeFlags(mailbox: string, uid: number, flags: string[]): Promise<void> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        await this.native.removeFlags(uid, flags);
        await this.native.closeMailbox();
    }

    /** Create a mailbox */
    async createmailbox(name: string): Promise<void> {
        await this.ensureConnected();
        await this.native.createMailbox(name);
    }

    /** Append a message to a mailbox */
    async appendMessage(mailbox: string, message: string | Uint8Array, flags: string[] = []): Promise<number | null> {
        await this.ensureConnected();
        const data = typeof message === "string" ? message : new TextDecoder().decode(message);
        return this.native.appendMessage(mailbox, data, flags);
    }

    /** Cached CAPABILITY set parsed at connect/login. Callers gate
     *  optional features (NOTIFY, QRESYNC, MOVE, ...) on this. */
    getCapabilities(): Set<string> {
        return this.native.getCapabilities();
    }

    /** Snapshot of the most recently SELECTed mailbox's info. Callers use this
     *  to read `highestModSeq` after any operation that did a SELECT — useful
     *  for seeding the QRESYNC modSeq watermark on the very first sync of a
     *  folder (before the QRESYNC path is eligible). */
    getCurrentMailboxInfo(): { uidValidity: number; uidNext: number; exists: number; highestModSeq?: number } {
        const info = this.native.getMailboxInfo?.();
        return info || { uidValidity: 0, uidNext: 0, exists: 0 };
    }

    /** RFC 5161 ENABLE — activates an IMAP extension for the remainder of
     *  the session. QRESYNC must be enabled before any SELECT for
     *  `* VANISHED` responses + automatic `MODSEQ` to start flowing.
     *  Returns the set of extensions the server confirmed are active. */
    async enable(extensions: string[]): Promise<Set<string>> {
        await this.ensureConnected();
        return this.native.enable(extensions);
    }

    /** Convenience: enable QRESYNC if the server advertises it. Returns
     *  true if QRESYNC is active on the session afterwards. Idempotent —
     *  safe to call once per connection at connect time. */
    async enableQresync(): Promise<boolean> {
        const caps = this.getCapabilities();
        if (!caps.has("QRESYNC")) return false;
        const enabled = await this.enable(["QRESYNC"]);
        return enabled.has("QRESYNC");
    }

    /** Fast resync of a mailbox via RFC 7162 QRESYNC. Caller supplies the
     *  `uidValidity` and last-seen `modSeq` from its prior visit; the
     *  server replies with `* VANISHED` for UIDs expunged since then and
     *  unsolicited `* FETCH` for state changes (flag updates etc.) since
     *  then, plus the current `HIGHESTMODSEQ` for the caller to persist
     *  as its new watermark.
     *
     *  Returns:
     *  - `uidValidityChanged`: server's UIDVALIDITY no longer matches; the
     *    caller's UID set is stale and MUST be full-resynced.
     *  - `vanishedUids`: authoritative deletion list. No client-side diff.
     *  - `changedMessages`: state-change FETCH responses since the prior
     *    modSeq — each carries `uid`, `flags`, and `modSeq` (and possibly
     *    other FETCH items the caller asked for).
     *  - `newHighestModSeq`: persist this for the next resync.
     *
     *  Pre-requisite: `enableQresync()` returned true once on the session.
     *  If the server doesn't support QRESYNC, fall back to the older
     *  `fetchMessagesSinceUid` / set-diff path. */
    async resyncFolder(
        mailbox: string,
        uidValidity: number,
        modSeq: number,
    ): Promise<{
        uidValidityChanged: boolean;
        vanishedUids: number[];
        changedMessages: FetchedMessage[];
        newHighestModSeq: number | undefined;
        exists: number;
        uidNext: number;
    }> {
        await this.ensureConnected();
        const result = await this.native.select(mailbox, { uidValidity, modSeq });
        const changedMessages = result.changedMessages.map(m => new FetchedMessage(m));
        return {
            uidValidityChanged: result.uidValidityChanged,
            vanishedUids: result.vanishedUids,
            changedMessages,
            newHighestModSeq: result.highestModSeq,
            exists: result.exists,
            uidNext: result.uidNext,
        };
    }

    /** Watch a mailbox for new messages (IDLE). Optionally engage RFC 5465
     *  NOTIFY so the server also pushes STATUS responses for non-selected
     *  mailboxes named in `opts.notifySpec` — `opts.onMailboxStatus` fires
     *  for each. Capability check is the caller's responsibility; pass
     *  notifySpec only when `getCapabilities().has("NOTIFY")`. */
    async watchMailbox(
        mailbox: string,
        onNew: (count: number) => void,
        opts?: {
            notifySpec?: string;
            onMailboxStatus?: (mailbox: string, data: proto.StatusData) => void;
            onExpunge?: () => void;
            /** A flag changed on a message in the watched mailbox — another
             *  client starred, read or unread something. */
            onFlagChange?: (seq: number, flags: string[]) => void;
        },
    ): Promise<() => Promise<void>> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        if (opts?.onMailboxStatus) this.native.onMailboxStatus = opts.onMailboxStatus;
        if (opts?.notifySpec) await this.native.notify(opts.notifySpec);
        return this.native.startIdle(onNew, opts?.onExpunge, opts?.onFlagChange);
    }

    /** Copy a message to another server (cross-account) */
    async moveMessageToServer(msg: any, fromMailbox: string, targetClient: CompatImapClient, toMailbox: string): Promise<void> {
        // Fetch source, append to target, delete from source
        await this.ensureConnected();
        await this.native.select(fromMailbox);
        const fetched = await this.native.fetchMessage(msg.uid, { source: true });
        if (!fetched || !fetched.source) throw new Error(`Message UID ${msg.uid} not found`);

        await targetClient.ensureConnected();
        await targetClient.native.appendMessage(toMailbox, fetched.source, [...fetched.flags]);
        await this.native.deleteMessage(msg.uid);
        await this.native.closeMailbox();
    }

    /** Rename a mailbox (via native access) */
    async renameMailbox(from: string, to: string): Promise<void> {
        await this.ensureConnected();
        await this.native.renameMailbox(from, to);
    }

    /** Delete a mailbox */
    async deleteMailbox(name: string): Promise<void> {
        await this.ensureConnected();
        await this.native.deleteMailbox(name);
    }

    /** Get flags for a UID — returns string[] for compatibility with old client */
    async getFlags(mailbox: string, uid: number): Promise<string[]> {
        await this.ensureConnected();
        await this.native.select(mailbox);
        const msg = await this.native.fetchMessage(uid, { source: false });
        await this.native.closeMailbox();
        return msg?.flags ? [...msg.flags] : [];
    }
}

/** IMAP quoted-string: backslash-escape `\` and `"`. The old inline
 *  `"${value}"` interpolation let a user-typed quote break the whole
 *  SEARCH command. */
function imapQuote(v: string): string {
    return `"${String(v).replace(/([\\"])/g, "\\$1")}"`;
}

/** Compile one criteria object's own keys (no not/or) to IMAP search keys. */
function searchKeys(c: any): string[] {
    const parts: string[] = [];
    const each = (v: any, fn: (s: string) => void): void => {
        for (const x of Array.isArray(v) ? v : [v]) if (x) fn(String(x));
    };
    if (c.from) each(c.from, v => parts.push(`FROM ${imapQuote(v)}`));
    if (c.to) each(c.to, v => parts.push(`TO ${imapQuote(v)}`));
    if (c.cc) each(c.cc, v => parts.push(`CC ${imapQuote(v)}`));
    if (c.subject) each(c.subject, v => parts.push(`SUBJECT ${imapQuote(v)}`));
    if (c.body) each(c.body, v => parts.push(`BODY ${imapQuote(v)}`));
    if (c.text) each(c.text, v => parts.push(`TEXT ${imapQuote(v)}`));
    if (c.since) parts.push(`SINCE ${formatDate(c.since)}`);
    if (c.before) parts.push(`BEFORE ${formatDate(c.before)}`);
    if (c.seen) parts.push("SEEN");
    if (c.unseen) parts.push("UNSEEN");
    if (c.flagged) parts.push("FLAGGED");
    if (c.unflagged) parts.push("UNFLAGGED");
    if (c.answered) parts.push("ANSWERED");
    if (c.draft) parts.push("DRAFT");
    return parts;
}

/** One criteria group as a single search-key (parenthesize multi-key). */
function keyGroup(c: any): string | null {
    const keys = searchKeys(c);
    if (keys.length === 0) return null;
    return keys.length === 1 ? keys[0] : `(${keys.join(" ")})`;
}

export function buildSearchString(criteria: any): string {
    const parts = searchKeys(criteria);
    // Exclusions: each entry compiles to `NOT <key-group>`; several entries
    // AND together (exclude anything matching any of them).
    const nots = criteria.not ? (Array.isArray(criteria.not) ? criteria.not : [criteria.not]) : [];
    for (const n of nots) {
        const g = keyGroup(n);
        if (g) parts.push(`NOT ${g}`);
    }
    // One OR group: IMAP OR is binary prefix, so [a,b,c] nests to
    // `OR a OR b c`.
    const ors = (Array.isArray(criteria.or) ? criteria.or : []).map(keyGroup).filter(Boolean) as string[];
    if (ors.length === 1) parts.push(ors[0]);
    else if (ors.length > 1) {
        let expr = ors[ors.length - 1];
        for (let i = ors.length - 2; i >= 0; i--) expr = `OR ${ors[i]} ${expr}`;
        parts.push(expr);
    }
    return parts.length > 0 ? parts.join(" ") : "ALL";
}

function formatDate(d: Date): string {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${d.getDate()}-${months[d.getMonth()]}-${d.getFullYear()}`;
}
